// src/newDashboard/shared/TextCustomers.jsx
//
// "Text your customers" on the Notifications page. $0.05 per text, paid
// from a prepaid balance. Only reaches customers who turned on texts in
// the app and live within the distance they picked. See functions/src/barTexts.ts.

import React, { useEffect, useState } from "react";
import { auth } from "../../firebase";

const URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/barTexts";

async function call(body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Something went wrong. Try again.");
  return data;
}

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const card = { background: "#0D0B1A", padding: "24px", borderRadius: "12px", maxWidth: "520px", marginBottom: "30px" };
const input = { width: "100%", padding: "12px", borderRadius: "8px", border: "none", fontSize: "15px", boxSizing: "border-box" };
const label = { display: "block", marginBottom: "8px", fontWeight: 600 };
const muted = { color: "#B8B0C8", fontSize: "15px", margin: 0 };
const smallBtn = {
  background: "#17131F", border: "1px solid #5A5070", color: "#fff", borderRadius: "8px",
  minHeight: "44px", padding: "0 14px", font: "600 15px Inter, sans-serif", cursor: "pointer",
};

export default function TextCustomers({ bars }) {
  const [barId, setBarId] = useState(bars[0]?.id || "");
  const [audience, setAudience] = useState("nearby");
  const [message, setMessage] = useState("");
  const [est, setEst] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("texts");
    if (p === "paid") return "Payment received. Your texting balance will update in a minute.";
    if (p === "canceled") return "No money was added.";
    return "";
  });

  const refresh = async () => {
    if (!barId) return;
    setError("");
    try {
      setEst(await call({ action: "estimate", barId, audience }));
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    setEst(null);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barId, audience]);

  const topup = async (amount) => {
    setBusy(true);
    setError("");
    try {
      const d = await call({ action: "topup", barId, amount, returnPath: window.location.pathname });
      if (d.url) window.location.href = d.url;
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const send = async () => {
    if (!est) return;
    const ok = window.confirm(
      `Text ${est.count} ${est.count === 1 ? "person" : "people"} for ${money(est.costCents)}?\n\n"${message.trim()}"\n\nThis can't be undone.`
    );
    if (!ok) return;
    setBusy(true);
    setError("");
    setNote("");
    try {
      const d = await call({ action: "send", barId, audience, message });
      setNote(`Sent to ${d.sent} of ${d.tried}. Cost ${money(d.costCents)}. You weren't charged for any that didn't go through.`);
      setMessage("");
      await refresh();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  if (!bars.length) return null;

  return (
    <div style={card}>
      <h3 style={{ margin: "0 0 6px" }}>Text your customers</h3>
      <p style={{ ...muted, marginBottom: "16px" }}>
        $0.05 per text. Goes only to customers who turned on texts in the app and live near you. Each person gets
        no more than a few texts a week from all bars, so yours get read.
      </p>

      {est && !est.ready && (
        <p style={{ color: "#FBBF24", fontWeight: 600, margin: "0 0 14px" }}>
          Texting is coming soon. We're finishing the phone carrier approval. You can see how many people you'd
          reach below.
        </p>
      )}

      {bars.length > 1 && (
        <>
          <label style={label}>Venue</label>
          <select value={barId} onChange={(e) => setBarId(e.target.value)} style={{ ...input, marginBottom: "14px" }}>
            {bars.map((b) => (
              <option key={b.id} value={b.id}>{b.name || "Unnamed Bar"}</option>
            ))}
          </select>
        </>
      )}

      <label style={label}>Who gets it</label>
      <select value={audience} onChange={(e) => setAudience(e.target.value)} style={{ ...input, marginBottom: "14px" }}>
        <option value="nearby">Everyone nearby who wants texts</option>
        <option value="favorites">Only people who saved my bar</option>
      </select>

      <label style={label}>Message</label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 120))}
        placeholder="Meat raffle tonight at 6! $5 burgers till 8."
        style={{ ...input, height: "80px", marginBottom: "6px" }}
      />
      <p style={{ ...muted, textAlign: "right", marginBottom: "14px" }}>
        {message.length}/120 · your bar's name and "Reply STOP to opt out" are added for you
      </p>

      <div style={{ background: "#17131F", borderRadius: "10px", padding: "14px", marginBottom: "14px" }}>
        {est ? (
          <>
            <p style={{ margin: "0 0 4px", fontSize: "17px" }}>
              Reaches <strong>{est.count.toLocaleString()}</strong> {est.count === 1 ? "person" : "people"} ·
              costs <strong>{money(est.costCents)}</strong>
            </p>
            <p style={muted}>Your balance: <strong style={{ color: "#fff" }}>{money(est.balanceCents)}</strong></p>
            {est.sentToday && <p style={{ ...muted, color: "#FBBF24" }}>You've sent today's text. You can send again tomorrow.</p>}
          </>
        ) : (
          <p style={muted}>Counting…</p>
        )}
      </div>

      <p style={{ ...muted, marginBottom: "8px" }}>Add money (it never expires):</p>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
        {[10, 25, 50, 100].map((a) => (
          <button key={a} type="button" style={smallBtn} disabled={busy || !est?.ready} onClick={() => topup(a)}>
            ${a}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={send}
        disabled={
          busy || !est?.ready || !est?.count || est?.sentToday || message.trim().length < 3 || (est?.balanceCents || 0) < (est?.costCents || 0)
        }
        style={{
          background: "linear-gradient(90deg, #FF3D81, #A855F7)", border: "none", padding: "14px", borderRadius: "8px",
          color: "#fff", fontWeight: 700, fontSize: "16px", cursor: "pointer", width: "100%",
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? "One moment…" : est ? `Send text for ${money(est.costCents)}` : "Send text"}
      </button>
      {est?.ready && est.count > 0 && (est.balanceCents || 0) < est.costCents && (
        <p style={{ ...muted, marginTop: "8px", color: "#FBBF24" }}>Add money to send this text.</p>
      )}

      {note && <p style={{ color: "#4ADE80", marginTop: "12px" }}>{note}</p>}
      {error && <p style={{ color: "#F472B6", marginTop: "12px" }}>{error}</p>}
    </div>
  );
}
